import axios from "axios";

const API_URL = process.env.REACT_APP_AWS_API_URL || '';

export const handlePhoneNumberCheck = async (phoneNumber, laundryId) => {
    try {
        const response = await axios.get(`${API_URL}/api/customer/check-phone`, {
            params: {
                operation: 'checkPhoneNumber',
                phoneNumber: phoneNumber,
                laundryId: laundryId,
            },
        });
        return {
            exists: response.data.exists,
            customerId: response.data.customerId,
            customerPaymentId: response.data.customerPaymentId,
            customerFirstName: response.data.firstName,
            specialInstructions: response.data.specialInstructions,
        };
    } catch (error) {
        console.error("Error checking phone number:", error);
        throw new Error("Unable to check phone number");
    }
};

// { exists, shared, firstName, hasPhone }. `shared` = the email is on several
// accounts and can't be used to sign in.
export const handleEmailCheck = async (email, laundryId) => {
    const response = await axios.post(`${API_URL}/api/auth/check-email`, {
        email,
        laundryId: laundryId || null,
    });
    return response.data;
};

// Send a sign-in code: by text to phoneNumber, or by email when `email` is
// given or channel is 'email' (then to the email on the phone's account).
export const initiateSignIn = async (phoneNumber, laundryId, { email = null, channel = null } = {}) => {
    try {
        const response = await axios.post(`${API_URL}/api/auth/send-otp`, {
            phoneNumber: phoneNumber || null,
            email: email || null,
            channel: channel || (email ? 'email' : 'sms'),
            laundryId: laundryId || null,
        });
        if (response.data.status === 'success') {
            return {
                isSignedIn: false,
                nextStep: { signInStep: 'CONFIRM_SIGN_IN' },
                channel: response.data.channel || 'sms',
                sentTo: response.data.sentTo || null,
            };
        }
        throw new Error("Failed to send OTP");
    } catch (error) {
        console.error("Error initiating sign in:", error);
        throw error;
    }
};

// Verify OTP — returns auth tokens on success
let _pendingPhone = null;
let _pendingLaundryId = null;
let _pendingEmail = null;
let _pendingChannel = 'sms';

export const setPendingAuth = (phone, laundryId, email = null, channel = 'sms') => {
    _pendingPhone = phone;
    _pendingLaundryId = laundryId;
    _pendingEmail = email;
    _pendingChannel = channel;
};

export const setPendingChannel = (channel) => {
    _pendingChannel = channel;
};

export const verifyOTP = async (otpCode) => {
    try {
        const response = await axios.post(`${API_URL}/api/auth/verify-otp`, {
            phoneNumber: _pendingPhone,
            email: _pendingEmail,
            channel: _pendingChannel,
            otpCode: otpCode,
            laundryId: _pendingLaundryId,
        });
        if (response.data.isSignedIn) {
            return {
                isSignedIn: true,
                accessToken: response.data.accessToken,
                refreshToken: response.data.refreshToken,
                user: response.data.user,
            };
        }
        return { isSignedIn: false, nextStep: { additionalInfo: { attemptsLeft: 2 } } };
    } catch (error) {
        if (error.response?.status === 401) {
            const detail = error.response.data.detail || '';
            if (detail.includes('Too many')) {
                return { isSignedIn: false, nextStep: { additionalInfo: { attemptsLeft: 0 } } };
            }
            return { isSignedIn: false, nextStep: { additionalInfo: { attemptsLeft: 2 } } };
        }
        throw new Error("Invalid OTP");
    }
};

// Sign up a new customer
export const initiateSignUp = async (laundryId, email, phoneNumber, firstName, lastName, inStore, receivePhoneNotification, isCommercial, billingEmail, referralCode, receiveMarketingTexts) => {
    try {
        const response = await axios.post(`${API_URL}/api/auth/customer-register`, {
            phoneNumber: phoneNumber || null,
            email,
            firstName,
            lastName,
            laundryId,
            receivePhoneNotification,
            receiveMarketingTexts: !!receiveMarketingTexts,
            isCommercial: isCommercial || false,
            billingEmail: billingEmail || '',
            referralCode: referralCode || null,
        });
        return {
            isSignUpComplete: response.data.isSignUpComplete || false,
            userId: response.data.userId,
            nextStep: response.data.nextStep || 'CONFIRM_SIGN_UP',
            otpChannel: response.data.otpChannel || 'sms',
            error: null,
        };
    } catch (error) {
        const detail = error.response?.data?.detail || error.message;
        return {
            isSignUpComplete: false,
            userId: null,
            nextStep: null,
            error: detail.includes("UsernameExists") ? "UsernameExistsException" : detail,
        };
    }
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('idToken')}` });

// Signed-in customer's basics (same shape as handlePhoneNumberCheck), for
// accounts signed in by email.
export const fetchCustomerProfile = async (laundryId) => {
    const response = await axios.get(`${API_URL}/api/auth/customer-profile`, {
        params: { laundryId },
        headers: authHeaders(),
    });
    return {
        exists: response.data.exists,
        customerId: response.data.customerId,
        customerPaymentId: response.data.customerPaymentId,
        customerFirstName: response.data.firstName,
        specialInstructions: response.data.specialInstructions,
        phoneNumber: response.data.phoneNumber,
    };
};

// Add a phone number to the signed-in account (driver contact; no texts).
export const saveCustomerPhone = async (phoneNumber) => {
    const response = await axios.put(`${API_URL}/api/auth/customer-phone`, { phoneNumber }, {
        headers: authHeaders(),
    });
    return response.data.phoneNumber;
};

// Compatibility: fetchAuthSession replacement
export const fetchAuthSession = async () => {
    const token = localStorage.getItem('idToken');
    const stored = localStorage.getItem('customerAuth');
    let loginId = null;
    if (stored) {
        try {
            const parsed = JSON.parse(stored);
            loginId = parsed.user?.phone;
        } catch (e) {}
    }
    return {
        tokens: {
            idToken: token,
            signInDetails: { loginId },
        },
    };
};
